import React, { useState } from "react";

// COMMON Import
import PrimaryButton from "../CommonButtons/PrimaryButton";

//  UTLIS Imports
import { linkIdentifier } from "../../../utils/linkIdentifier";
import {
  GlobalConfiguration,
  IntervalConfiguration,
} from "../../../utils/configurationProvider";

// Suggestions of props for component while using the components -------------------------------------
/**
 * @typedef {((e: React.ChangeEvent<HTMLInputElement>) => void)} OnInputEventHandler
 */
/**
 * @typedef {((e: React.MouseEvent<HTMLButtonElement, MouseEvent>?) => void)} OnClickEventHandler
 */
/**
 * @typedef {("button" | "submit")} BtnType
 */
/**
 * @typedef {("white" | "black")} HoverOption
 */
/**
 * @typedef {("default" | "outlined" | "filled")} VariantType
 */
/**
 * @param {Object} props
 * @param {string} props.headerTitle - Main title text to display at the top of popup
 * @param {string} [props.headerFontWeight] - Font weight for header title eg:- "normal", "semibold", "bold"
 * @param {string} [props.headerFontColor] - Text color class for header eg:- "text-primary", "text-secondary", "failed"
 * @param {string} [props.textSize] - Font size class for header eg:- "font-sm", "font-md", "font-lg"
 * @param {string} [props.headerWithIcon] - Icon/SVG image path to display next to header title
 * @param {string} [props.secondHeaderTitle] - Optional secondary header text below main title
 * @param {string} [props.secondHeaderFontWeight] - Font weight for secondary header eg:- "normal", "semibold"
 * @param {string} [props.secondHeaderFontColor] - Text color class for secondary header eg:- "text-primary"
 * @param {string} [props.secondHeaderTextSize] - Font size class for secondary header eg:- "font-sm"
 * @param {string} [props.subtext] - Subtitle/description text below headers
 * @param {string} [props.noteText] - Note/help text to display below input field
 * @param {string} props.btnOneText - Text to display on first button (left button)
 * @param {BtnType} [props.btnOneBtnType] - Button type for button one eg:- "button" or "submit"
 * @param {OnClickEventHandler} props.btnOneHandleFunction - Handle function when button one is clicked
 * @param {boolean} [props.btnOneIsDisabled] - Disable button one (true/false)
 * @param {string} [props.btnOneEnabledTextColor] - Text color when button one is enabled (CSS variable)
 * @param {string} [props.btnOneDisabledTextColor] - Text color when button one is disabled (CSS variable)
 * @param {string} [props.btnOneEnabledBgColor] - Background color when button one is enabled (CSS variable)
 * @param {string} [props.btnOneDisabledBgColor] - Background color when button one is disabled (CSS variable)
 * @param {string} [props.btnOneOutLineColor] - Border/outline color for button one (CSS variable)
 * @param {boolean} [props.btnOneFullWidthRequired] - Make button one full width (true/false)
 * @param {HoverOption} [props.btnOneHoverOption] - Hover effect option for button one eg:- "white" or "black"
 * @param {boolean} [props.btnOneIsLoading] - Show loading state on button one (true/false)
 * @param {React.RefObject} [props.btnOneButtonRef] - Button ref for button one (optional)
 * @param {boolean} [props.btnOneInActiveVariantNeeded] - Use inactive variant for button one (true/false)
 * @param {boolean} [props.btnOneIsLoaderNeeded] - Show loader on button one (true/false)
 * @param {string} props.btnTwoText - Text to display on second button (right button)
 * @param {BtnType} [props.btnTwoBtnType] - Button type for button two eg:- "button" or "submit"
 * @param {OnClickEventHandler} props.btnTwoHandleFunction - Handle function when button two is clicked
 * @param {boolean} [props.btnTwoIsDisabled] - Disable button two (true/false)
 * @param {string} [props.btnTwoEnabledTextColor] - Text color when button two is enabled (CSS variable)
 * @param {string} [props.btnTwoDisabledTextColor] - Text color when button two is disabled (CSS variable)
 * @param {string} [props.btnTwoEnabledBgColor] - Background color when button two is enabled (CSS variable)
 * @param {string} [props.btnTwoDisabledBgColor] - Background color when button two is disabled (CSS variable)
 * @param {string} [props.btnTwoOutLineColor] - Border/outline color for button two (CSS variable)
 * @param {boolean} [props.btnTwoFullWidthRequired] - Make button two full width (true/false)
 * @param {HoverOption} [props.btnTwoHoverOption] - Hover effect option for button two eg:- "white" or "black"
 * @param {boolean} [props.btnTwoIsLoading] - Show loading state on button two (true/false)
 * @param {React.RefObject} [props.btnTwoButtonRef] - Button ref for button two (optional)
 * @param {boolean} [props.btnTwoInActiveVariantNeeded] - Use inactive variant for button two (true/false)
 * @param {boolean} [props.btnTwoIsLoaderNeeded] - Show loader on button two (true/false)
 * @returns {JSX.Element}
 */
const SmallPopup = ({
  headerTitle,
  headerWithIcon,
  secondHeaderTitle,
  noteText,
  subtext,
  btnOneText,
  btnOneBtnType,
  btnOneDisabledTextColor,
  btnOneDisabledBgColor,
  btnOneIsLoading,
  btnOneButtonRef,
  btnOneInActiveVariantNeeded,
  btnOneIsLoaderNeeded,
  btnOneHandleFunction,
  btnOneEnabledBgColor,
  btnOneOutLineColor,
  btnOneEnabledTextColor,
  btnOneIsDisabled,
  btnOneHoverOption,
  btnOneFullWidthRequired,
  btnTwoText,
  btnTwoBtnType,
  btnTwoDisabledTextColor,
  btnTwoIsLoading,
  btnTwoButtonRef,
  btnTwoInActiveVariantNeeded,
  btnTwoIsLoaderNeeded,
  btnTwoHandleFunction,
  btnTwoEnabledBgColor,
  btnTwoDisabledBgColor,
  btnTwoOutLineColor,
  btnTwoEnabledTextColor,
  btnTwoIsDisabled,
  btnTwoFullWidthRequired,
  btnTwoHoverOption,
  noteColor,
}) => {
  const [animate, setAnimate] = useState(false);

  const toSentenceCase = (text = "") =>
    text.charAt(GlobalConfiguration?.lengthCheckZero).toUpperCase() +
    text.slice(GlobalConfiguration?.lengthCheckOne).toLowerCase();

  const needsFullStop = (text = "") => !/[.?!]\s*$/.test(text);

  return (
    <div
      className="flex w-full justify-center items-center m-auto top-1/2 left-1/2 fixed z-z-index-modal px-padding-md py-padding-sm"
      style={{
        transform: "translate(-50%, -50%)",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={`h-fit w-full overflow-hidden Qmd:w-small-popup-width max-h-graphic_rectangle_height_xl flex flex-col px-padding-md py-padding-md bg-primary-bg rounded-border-radius-md animate-none ${animate ? "Qmd:animate-slideInFromLeftSmallPopUp" : "Qmd:animate-slideInFromRightSmallPopUp"} `}
      >
        <div className="">
          <div className=" flex flex-col gap-gap-xs">
            <div className="flex gap-gap-sm">
              <img
                src={headerWithIcon}
                alt=""
                className={`h-icon-md w-icon-md ${!headerWithIcon && "hidden"} `}
              />

              <p className={`text-text-primary  text-font-md font-semibold`}>
                {toSentenceCase(headerTitle)}
              </p>
            </div>
            <div className={`${!secondHeaderTitle && "hidden"}`}>
              <p className={`text-font-sm text-text-primary  font-semibold`}>
                {linkIdentifier(secondHeaderTitle)}
              </p>
            </div>

            {subtext && (
              <span className="text-font-sm text-text-secondary font-normal ">
                {linkIdentifier(subtext)}
                {needsFullStop(subtext) && <span>.</span>}
              </span>
            )}
          </div>
          <div
            className={`${noteColor ? `text-${noteColor}` : "text-text-secondary"} text-font-xs font-normal leading-normal ${!noteText ? "hidden" : "pt-padding-sm"}`}
          >
            <span className="">Note - </span>
            {noteText && (
              <span>
                {linkIdentifier(noteText)}
                {needsFullStop(noteText) && <span>.</span>}
              </span>
            )}
          </div>
          <div className="w-full gap-gap-sm flex justify-end pt-padding-md">
            <PrimaryButton
              btnText={btnOneText}
              btnType={btnOneBtnType}
              handleFunction={() => {
                if (!btnOneIsDisabled) {
                  setAnimate(true);
                  setTimeout(() => {
                    btnOneHandleFunction();
                  }, IntervalConfiguration.intervalSemiMid);
                }
              }}
              isDisabled={btnOneIsDisabled}
              enabledTextColor={btnOneEnabledTextColor}
              disabledTextColor={btnOneDisabledTextColor}
              enabledBgColor={btnOneEnabledBgColor}
              disabledBgColor={btnOneDisabledBgColor}
              outLineColor={btnOneOutLineColor}
              fullWidthRequired={btnOneFullWidthRequired}
              hoverOption={btnOneHoverOption}
              isLoading={btnOneIsLoading}
              buttonRef={btnOneButtonRef}
              inActiveVariantNeeded={btnOneInActiveVariantNeeded}
              isLoaderNeeded={btnOneIsLoaderNeeded}
            />
            <PrimaryButton
              btnText={btnTwoText}
              handleFunction={() => {
                if (!btnTwoIsDisabled) {
                  setAnimate(true);
                  setTimeout(() => {
                    btnTwoHandleFunction();
                  }, IntervalConfiguration.intervalSemiMid);
                }
              }}
              enabledBgColor={btnTwoEnabledBgColor}
              outLineColor={btnTwoOutLineColor}
              enabledTextColor={btnTwoEnabledTextColor}
              isDisabled={btnTwoIsDisabled}
              fullWidthRequired={btnTwoFullWidthRequired}
              hoverOption={btnTwoHoverOption}
              btnType={btnTwoBtnType}
              disabledTextColor={btnTwoDisabledTextColor}
              disabledBgColor={btnTwoDisabledBgColor}
              isLoading={btnTwoIsLoading}
              buttonRef={btnTwoButtonRef}
              inActiveVariantNeeded={btnTwoInActiveVariantNeeded}
              isLoaderNeeded={btnTwoIsLoaderNeeded}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmallPopup;
